import React, { useState, useEffect } from 'react';
import { SidebarProvider } from '../../components/navigation/Sidebar';
import Sidebar from '../../components/navigation/Sidebar';
import QuickActions from '../../components/navigation/QuickActions';
import DateRangeSelector from '../../components/navigation/DateRangeSelector';
import MetricCard from './components/MetricCard';
import SiteHealthPanel from './components/SiteHealthPanel';
import RecentActivityFeed from './components/RecentActivityFeed';
import AutomationErrorAlerts from './components/AutomationErrorAlerts';
import ContentPerformanceChart from './components/ContentPerformanceChart';
import ContentPerformanceTable from './components/ContentPerformanceTable';
import SiteSelector from './components/SiteSelector';
import ContentTypeFilter from './components/ContentTypeFilter';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const WordPressContentMonitor = () => {
  const [selectedSite, setSelectedSite] = useState('all');
  const [activeFilter, setActiveFilter] = useState('all');
  const [lastUpdated, setLastUpdated] = useState(new Date());

  useEffect(() => {
    const interval = setInterval(() => {
      setLastUpdated(new Date());
    }, 600000);

    return () => clearInterval(interval);
  }, []);

  const sites = [
    {
      id: 'site1',
      name: 'Tech Blog Pro',
      url: 'techblog.example.com',
      status: 'online',
      uptime: '99.9%',
      responseTime: '245ms'
    },
    {
      id: 'site2',
      name: 'Marketing Hub',
      url: 'marketing.example.com',
      status: 'online',
      uptime: '99.7%',
      responseTime: '312ms'
    },
    {
      id: 'site3',
      name: 'Business Insights',
      url: 'business.example.com',
      status: 'warning',
      uptime: '98.5%',
      responseTime: '487ms'
    }
  ];

  const metrics = [
    {
      title: 'Total Articles Published',
      value: '1,247',
      change: '+12.5%',
      changeType: 'positive',
      icon: 'FileText',
      iconColor: 'bg-primary',
      trend: 'vs last month'
    },
    {
      title: 'Average Engagement',
      value: '3,842',
      change: '+8.3%',
      changeType: 'positive',
      icon: 'Heart',
      iconColor: 'bg-accent',
      trend: 'per article'
    },
    {
      title: 'Site Health Score',
      value: '94/100',
      change: '+2 points',
      changeType: 'positive',
      icon: 'Activity',
      iconColor: 'bg-success',
      trend: 'all sites avg'
    },
    {
      title: 'Automation Success',
      value: '97.8%',
      change: '-0.5%',
      changeType: 'negative',
      icon: 'Zap',
      iconColor: 'bg-warning',
      trend: 'last 30 days'
    }
  ];

  const chartData = [
    { date: '11/01', published: 42, engagement: 3245 },
    { date: '11/05', published: 38, engagement: 3567 },
    { date: '11/10', published: 45, engagement: 3892 },
    { date: '11/15', published: 51, engagement: 4123 },
    { date: '11/20', published: 47, engagement: 3978 },
    { date: '11/25', published: 53, engagement: 4256 }
  ];

  const recentActivities = [
    {
      id: 1,
      title: '10 Ways to Boost Your Marketing ROI in 2025',
      site: 'Marketing Hub',
      type: 'published',
      timestamp: '2 minutes ago',
      badge: 'Auto'
    },
    {
      id: 2,
      title: 'Understanding AI in Modern Business Operations',
      site: 'Tech Blog Pro',
      type: 'scheduled',
      timestamp: '15 minutes ago',
      badge: 'Auto'
    },
    {
      id: 3,
      title: 'Customer Success Stories: Q4 2024 Highlights',
      site: 'Business Insights',
      type: 'published',
      timestamp: '1 hour ago',
      badge: 'Manual'
    },
    {
      id: 4,
      title: 'The Future of Remote Work Technology',
      site: 'Tech Blog Pro',
      type: 'failed',
      timestamp: '2 hours ago',
      badge: 'Auto'
    },
    {
      id: 5,
      title: 'Digital Marketing Trends for Small Businesses',
      site: 'Marketing Hub',
      type: 'updated',
      timestamp: '3 hours ago',
      badge: 'Manual'
    }
  ];

  const automationAlerts = [
    {
      id: 1,
      severity: 'critical',
      title: 'Publishing Failed',
      message: 'Article "The Future of Remote Work Technology" failed to publish on Tech Blog Pro due to authentication error.',
      timestamp: '2 hours ago'
    },
    {
      id: 2,
      severity: 'warning',
      title: 'Slow Response Time',
      message: 'Business Insights site experiencing elevated response times (487ms avg). Consider checking server resources.',
      timestamp: '4 hours ago'
    },
    {
      id: 3,
      severity: 'info',
      title: 'Scheduled Maintenance',
      message: 'Marketing Hub will undergo routine maintenance on 11/28 at 2:00 AM EST. Automated publishing will be paused.',
      timestamp: '1 day ago'
    }
  ];

  const articles = [
    {
      id: 1,
      title: '10 Ways to Boost Your Marketing ROI in 2025',
      site: 'Marketing Hub',
      category: 'Marketing Strategy',
      publishedDate: '11/26/2024',
      publishedTime: '09:30 AM',
      views: '12.5K',
      engagement: '847',
      seoScore: 92,
      status: 'published',
      automationType: 'auto'
    },
    {
      id: 2,
      title: 'Understanding AI in Modern Business Operations',
      site: 'Tech Blog Pro',
      category: 'Technology',
      publishedDate: '11/26/2024',
      publishedTime: '08:15 AM',
      views: '8.3K',
      engagement: '623',
      seoScore: 88,
      status: 'published',
      automationType: 'auto'
    },
    {
      id: 3,
      title: 'Customer Success Stories: Q4 2024 Highlights',
      site: 'Business Insights',
      category: 'Case Studies',
      publishedDate: '11/25/2024',
      publishedTime: '02:45 PM',
      views: '15.2K',
      engagement: '1.2K',
      seoScore: 95,
      status: 'published',
      automationType: null
    },
    {
      id: 4,
      title: 'The Future of Remote Work Technology',
      site: 'Tech Blog Pro',
      category: 'Technology',
      publishedDate: '11/25/2024',
      publishedTime: '11:00 AM',
      views: '0',
      engagement: '0',
      seoScore: 78,
      status: 'failed',
      automationType: 'auto'
    },
    {
      id: 5,
      title: 'Digital Marketing Trends for Small Businesses',
      site: 'Marketing Hub',
      category: 'Marketing Strategy',
      publishedDate: '11/24/2024',
      publishedTime: '03:20 PM',
      views: '9.7K',
      engagement: '542',
      seoScore: 85,
      status: 'published',
      automationType: 'auto'
    },
    {
      id: 6,
      title: 'Building a Strong Company Culture in 2025',
      site: 'Business Insights',
      category: 'Leadership',
      publishedDate: '11/24/2024',
      publishedTime: '10:30 AM',
      views: '11.4K',
      engagement: '789',
      seoScore: 90,
      status: 'published',
      automationType: null
    },
    {
      id: 7,
      title: 'Cybersecurity Best Practices for Startups',
      site: 'Tech Blog Pro',
      category: 'Security',
      publishedDate: '11/23/2024',
      publishedTime: '01:15 PM',
      views: '7.8K',
      engagement: '456',
      seoScore: 82,
      status: 'published',
      automationType: 'auto'
    },
    {
      id: 8,
      title: 'Content Marketing ROI: Measuring Success',
      site: 'Marketing Hub',
      category: 'Content Marketing',
      publishedDate: '11/27/2024',
      publishedTime: '09:00 AM',
      views: '0',
      engagement: '0',
      seoScore: 87,
      status: 'scheduled',
      automationType: 'auto'
    },
    {
      id: 9,
      title: 'Employee Retention Strategies That Work',
      site: 'Business Insights',
      category: 'HR & Management',
      publishedDate: '11/22/2024',
      publishedTime: '04:45 PM',
      views: '13.6K',
      engagement: '934',
      seoScore: 93,
      status: 'published',
      automationType: null
    },
    {
      id: 10,
      title: 'Cloud Computing Trends for Enterprise',
      site: 'Tech Blog Pro',
      category: 'Technology',
      publishedDate: '11/22/2024',
      publishedTime: '11:30 AM',
      views: '10.2K',
      engagement: '678',
      seoScore: 89,
      status: 'published',
      automationType: 'auto'
    }
  ];

  const filterCounts = {
    all: articles?.length,
    published: articles?.filter(a => a?.status === 'published')?.length,
    scheduled: articles?.filter(a => a?.status === 'scheduled')?.length,
    draft: articles?.filter(a => a?.status === 'draft')?.length,
    failed: articles?.filter(a => a?.status === 'failed')?.length
  };

  const handleDismissAlert = (alertId) => {
    console.log('Dismissing alert:', alertId);
  };

  const handleRetryAlert = (alertId) => {
    console.log('Retrying alert:', alertId);
  };

  const handleViewDetails = (articleId) => {
    console.log('Viewing article details:', articleId);
  };

  const handleExportReport = () => {
    console.log('Exporting content performance report');
  };

  const handleRefreshData = () => {
    setLastUpdated(new Date());
    console.log('Refreshing dashboard data');
  };

  return (
    <SidebarProvider>
      <div className="flex min-h-screen bg-background">
        <Sidebar />
        
        <main className="main-content">
          <div className="max-w-[1920px] mx-auto">
            <div className="mb-8">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h1 className="text-3xl font-bold text-foreground mb-2">WordPress Content Monitor</h1>
                  <p className="text-muted-foreground">Multi-site content performance tracking and publishing automation oversight</p>
                </div>
                <div className="flex items-center gap-3">
                  <Button
                    variant="outline"
                    iconName="RefreshCw"
                    iconPosition="left"
                    onClick={handleRefreshData}
                  >
                    Refresh
                  </Button>
                  <Button
                    variant="default"
                    iconName="Download"
                    iconPosition="left"
                    onClick={handleExportReport}
                  >
                    Export Report
                  </Button>
                </div>
              </div>

              <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4 mb-6">
                <SiteSelector
                  sites={sites}
                  selectedSite={selectedSite}
                  onSiteChange={setSelectedSite}
                  connectionStatus="connected"
                />
                <div className="flex items-center gap-3">
                  <DateRangeSelector />
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Icon name="Clock" size={16} />
                    <span>Last updated: {lastUpdated?.toLocaleTimeString()}</span>
                  </div>
                </div>
              </div>

              <ContentTypeFilter
                activeFilter={activeFilter}
                onFilterChange={setActiveFilter}
                counts={filterCounts}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              {metrics?.map((metric, index) => (
                <MetricCard key={index} {...metric} />
              ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-8">
              <div className="lg:col-span-8">
                <ContentPerformanceChart data={chartData} />
              </div>
              <div className="lg:col-span-4 space-y-6">
                <SiteHealthPanel sites={sites} />
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-8">
              <div className="lg:col-span-6">
                <RecentActivityFeed activities={recentActivities} />
              </div>
              <div className="lg:col-span-6">
                <AutomationErrorAlerts
                  alerts={automationAlerts}
                  onDismiss={handleDismissAlert}
                  onRetry={handleRetryAlert}
                />
              </div>
            </div>

            <ContentPerformanceTable
              articles={articles}
              onViewDetails={handleViewDetails}
            />
          </div>
        </main>

        <QuickActions />
      </div>
    </SidebarProvider>
  );
};

export default WordPressContentMonitor;